/**
 * AkerPreset — Custom PrimeNG theme preset based on the $aker-material-light palette.
 *
 * Color sources (from src/styles/aker/_theme-colors.scss):
 *   primary   : #081E32 navy family   (Material tones 10–95)
 *   secondary : #A9E0FF teal-blue family (Material tones 10–95)
 *   tertiary  : #FF9900 orange family  (Material tones 10–95)
 *   neutral   : #D4D4D4 grey family    (Material tones 10–99)
 *
 * Architecture:
 *   primitive  → raw color scales (aker-specific palettes added here)
 *   semantic   → primary palette + surface + interactive state tokens
 *   extend     → custom aker shortcut tokens for secondary & tertiary accents
 */

import { definePreset } from '@primeuix/themes';
import Nora from '@primeuix/themes/nora';

const AkerPreset = definePreset(Nora, {
  // ─────────────────────────────────────────────────────────────────
  // PRIMITIVE TOKENS
  // Raw color scales that semantic tokens can reference.
  // ─────────────────────────────────────────────────────────────────
  primitive: {
    /**
     * Secondary palette — teal-blue family.
     * Source: $_palettes → secondary in _theme-colors.scss
     */
    akerSecondary: {
      50: '#e2f3ff',
      100: '#c1e8ff',
      200: '#97ceec',
      300: '#7cb2d0',
      400: '#6197b4',
      500: '#467e99',
      600: '#2a657f', // tone-40 (M3 main secondary)
      700: '#1b5873', // tone-35
      800: '#054d66', // tone-30
      900: '#003548', // tone-20
      950: '#001e2b', // tone-10
    },

    /**
     * Tertiary palette — orange/amber family.
     * Source: $_palettes → tertiary in _theme-colors.scss
     */
    akerTertiary: {
      50: '#ffe3ba',
      100: '#ffdcbd',
      200: '#ffb86f',
      300: '#ff9900', // tone-70 (vibrant orange — also --aker-tertiary-70)
      400: '#d17d00', // tone-60
      500: '#ad6600', // tone-50
      600: '#8a5100', // tone-40 (M3 main tertiary)
      700: '#794600', // tone-35
      800: '#693c00', // tone-30
      900: '#4a2800', // tone-20
      950: '#2c1600', // tone-10
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // SEMANTIC TOKENS
  // ─────────────────────────────────────────────────────────────────
  semantic: {
    /**
     * Primary palette — navy family (11-stop scale for PrimeNG).
     * Mapped from aker primary tones in _theme-colors.scss.
     * PrimeNG uses 50–950; tone-40 (#4d6077) is the "main" colour.
     */
    fontFamily: 'Arial, sans-serif',
    primary: {
      50: '#e9f1ff', // tone-95
      100: '#d0e4ff', // tone-90
      200: '#b4c8e3', // tone-80
      300: '#99adc7', // tone-70
      400: '#7f92ab', // tone-60
      500: '#657991', // tone-50
      600: '#4d6077', // tone-40  ← main primary (--aker-dark-blue-40)
      700: '#41546a', // tone-35
      800: '#35485e', // tone-30
      900: '#1e3247', // tone-20  (--aker-dark-blue-20)
      950: '#081e32', // tone-10  (--aker-dark-blue-10, header background)
    },

    /**
     * Focus ring — use aker primary colour for keyboard navigation.
     */
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
    },
    /**
     * Form field sizing — matches Nora defaults with aker border-radius.
     */
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.625rem',
        paddingY: '0.375rem',
      },
      lg: {
        fontSize: '1.125rem',
        paddingX: '0.875rem',
        paddingY: '0.625rem',
      },
      borderRadius: '8px',
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.color}',
        offset: '-1px',
        shadow: 'none',
      },
      transitionDuration: '{transition.duration}',
    },

    /**
     * Color-scheme tokens (light only — dark mode disabled via darkModeSelector:false).
     */
    colorScheme: {
      light: {
        /**
         * Surface scale — mapped from aker neutral tones.
         * Used for backgrounds, cards, overlays, etc.
         */
        surface: {
          0: '#ffffff',
          50: '#fcfcfc', // neutral-99
          100: '#faf9f9', // neutral-98  (--neutral-98)
          200: '#f1f1f0', // neutral-95
          300: '#e2e2e2', // neutral-90
          400: '#c6c6c6', // neutral-80
          500: '#ababab', // neutral-70
          600: '#909191', // neutral-60  (--neutral-60)
          700: '#767777', // neutral-50  (--neutral-50)
          800: '#5d5e5f', // neutral-40  (--neutral-40)
          900: '#454747', // neutral-30  (--neutral-30)
          950: '#2f3131', // neutral-20  (--neutral-20)
        },

        /**
         * Primary interactive tokens.
         * These power button colors, link underlines, selection highlights, etc.
         */
        primary: {
          color: '#4d6077', // tone-40 — main navy (--aker-dark-blue-40)
          contrastColor: '#ffffff',
          hoverColor: '#35485e', // tone-30 (darker on hover)
          activeColor: '#1e3247', // tone-20 (darkest on press — --aker-dark-blue-20)
        },

        /**
         * Highlight tokens — used for selected rows, focused items, etc.
         */
        highlight: {
          background: '#d0e4ff', // tone-90 (soft navy)
          focusBackground: '#b4c8e3', // tone-80 (slightly deeper)
          color: '#081e32', // tone-10 (dark text on highlight — --aker-dark-blue-10)
          focusColor: '#081e32',
        },

        /**
         * Form field defaults — border uses primary on hover/focus
         * so inputs visually align with the aker navy brand.
         */
        formField: {
          hoverBorderColor: '{primary.color}',
          focusBorderColor: '{primary.color}',
          floatLabelColor: '#8395A4', // based on the figma sketch
          floatLabelActiveColor: '#8395A4', // based on the figma sketch
          borderColor: '#ACB5BD', // based on the figma sketch
        },
        navigation: {
          item: {
            // The active/selected item (routerLinkActive)
            activeBackground: '#081E32', // ← your dark navy
            activeColor: '#ffffff',

            // Hover state
            focusBackground: '#243044',
            focusColor: '#ffffff',

            // Default (non-active) item text
            color: '{text.color}',
          },
        },
      },

      // dark intentionally omitted — darkModeSelector is false in app.module.ts
    },
  },

  // ─────────────────────────────────────────────────────────────────
  // EXTEND — custom aker accent tokens
  // Generates CSS variables:  --p-aker-secondary-color, etc.
  // Use these in component SCSS to reference secondary/tertiary accents
  // without coupling to the Material CSS custom property names.
  // ─────────────────────────────────────────────────────────────────
  extend: {
    aker: {
      secondary: {
        color: '#2a657f', // tone-40  --p-aker-secondary-color
        hoverColor: '#1b5873', // tone-35  --p-aker-secondary-hover-color
        lightColor: '#c1e8ff', // tone-90  --p-aker-secondary-light-color
        darkColor: '#003548', // tone-20  --p-aker-secondary-dark-color
      },
      tertiary: {
        color: '#ff9900', // tone-70  --p-aker-tertiary-color  (vibrant orange)
        hoverColor: '#d17d00', // tone-60  --p-aker-tertiary-hover-color
        activeColor: '#8a5100', // tone-40  --p-aker-tertiary-active-color
        lightColor: '#ffdcbd', // tone-90  --p-aker-tertiary-light-color
        darkColor: '#4a2800', // tone-20  --p-aker-tertiary-dark-color
      },
    },
  },
});

export default AkerPreset;
